import { createContext, useContext } from 'react';

export type KioskScreen =
  | 'home'
  | 'checkout-user'
  | 'checkout-confirm'
  | 'checkin-confirm'
  | 'success'
  | 'info';

export type InfoVariant = 'error' | 'warning' | 'info';

export interface KioskState {
  screen: KioskScreen;
  kioskCode: string;
  locationId: string | null;
  locationName: string;
  equipmentId: string | null;
  userId: string | null;
  infoMessage: string | null;
  infoVariant: InfoVariant | null;
  infoDetails: string | null;
  successMessage: string | null;
}

export type KioskAction =
  | { type: 'EQUIPMENT_SCANNED'; equipmentId: string }
  | { type: 'USER_SCANNED'; userId: string }
  | { type: 'ROUTE_CHECKOUT' }
  | { type: 'ROUTE_CHECKIN' }
  | { type: 'SUCCESS'; message: string }
  | { type: 'SHOW_INFO'; message: string; variant: InfoVariant; details?: string }
  | { type: 'RESET' };

export const initialState: KioskState = {
  screen: 'home',
  kioskCode: '',
  locationId: null,
  locationName: '',
  equipmentId: null,
  userId: null,
  infoMessage: null,
  infoVariant: null,
  infoDetails: null,
  successMessage: null,
};

export function kioskReducer(state: KioskState, action: KioskAction): KioskState {
  switch (action.type) {
    case 'EQUIPMENT_SCANNED':
      return { ...state, equipmentId: action.equipmentId };

    case 'USER_SCANNED':
      return { ...state, userId: action.userId, screen: 'checkout-confirm' };

    case 'ROUTE_CHECKOUT':
      return { ...state, screen: 'checkout-user' };

    case 'ROUTE_CHECKIN':
      return { ...state, screen: 'checkin-confirm' };

    case 'SUCCESS':
      return {
        ...state,
        screen: 'success',
        successMessage: action.message,
        infoMessage: null,
        infoVariant: null,
        infoDetails: null,
      };

    case 'SHOW_INFO':
      return {
        ...state,
        screen: 'info',
        infoMessage: action.message,
        infoVariant: action.variant,
        infoDetails: action.details ?? null,
        successMessage: null,
      };

    case 'RESET':
      return { ...initialState, kioskCode: state.kioskCode, locationId: state.locationId, locationName: state.locationName };

    default:
      return state;
  }
}

export interface KioskContextValue {
  state: KioskState;
  scanEquipment: (equipmentId: string) => void;
  scanUser: (userId: string) => void;
  routeCheckout: () => void;
  routeCheckin: () => void;
  showSuccess: (message: string) => void;
  showInfo: (message: string, variant: InfoVariant, details?: string) => void;
  reset: () => void;
}

export const KioskContext = createContext<KioskContextValue | null>(null);

export function useKiosk() {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
}
